import { lazy, Suspense, type ReactNode } from "react";
import { Redirect, Route, Switch, useLocation, useSearch } from "wouter";
import { SessionProvider, safeNext, useSession } from "./lib/session.tsx";
import { Skeleton } from "./components/ui.tsx";
import { LoginPage } from "./pages/LoginPage.tsx";
import { RequestResetPage, ResetPasswordPage } from "./pages/ResetPages.tsx";
import { PlansPage } from "./pages/PlansPage.tsx";
import { ProfilePage } from "./pages/ProfilePage.tsx";
import { NotFoundPage } from "./pages/NotFoundPage.tsx";
import { GuardProvider } from "./lib/guard.tsx";
import { ToastProvider } from "./components/toast.tsx";
import { GuardedLink, PlanShell } from "./components/plan-shell.tsx";
import { RouteAnnouncer, RouteEffects, usePageTitle } from "./lib/nav.tsx";
import { InvitationPage } from "./pages/InvitationPage.tsx";
import { AccessEndedPage } from "./pages/AccessEndedPage.tsx";
import { DashboardPage } from "./pages/DashboardPage.tsx";
import { SettingsPage } from "./pages/settings/SettingsPage.tsx";
import { PlanningPage } from "./pages/PlanningPage.tsx";
import { InitiativePage } from "./pages/InitiativePage.tsx";
import { ReviewQueuePage } from "./pages/ReviewQueuePage.tsx";
import { RevisionPage } from "./pages/RevisionPage.tsx";
import { AgendaPage } from "./pages/AgendaPage.tsx";
import { AgendaEventPage } from "./pages/AgendaEventPage.tsx";
import { ReportsPage } from "./pages/ReportsPage.tsx";
import { AssessmentsPage } from "./pages/maturity/AssessmentsPage.tsx";
import { TemplatesPage } from "./pages/maturity/TemplatesPage.tsx";
import { TemplateVersionPage } from "./pages/maturity/TemplateVersionPage.tsx";
import { ScenarioPage } from "./pages/maturity/ScenarioPage.tsx";
import { MaturityDashboardPage } from "./pages/maturity/MaturityDashboardPage.tsx";
import { AssignmentsPage } from "./pages/maturity/AssignmentsPage.tsx";
import { WorkshopCategoryPage, WorkshopDiagnosisPage } from "./pages/WorkshopDiagnosisPage.tsx";
import { MaturityReportsPage } from "./pages/maturity/MaturityReportsPage.tsx";

function FullPageLoading() {
  return (
    <div className="mx-auto max-w-md px-4 py-16">
      <Skeleton lines={3} />
    </div>
  );
}

/** Protected routes: no session → /login?next=<current path> (only safe paths are kept). */
function Protected({ children }: { children: ReactNode }) {
  const { user, loading } = useSession();
  const [location] = useLocation();
  if (loading) return <FullPageLoading />;
  if (!user) return <Redirect to={`/login?next=${encodeURIComponent(location)}`} replace />;
  return <>{children}</>;
}

/** Anonymous-only routes: a logged-in visitor goes to the plans (or a safe `next`). */
function AnonymousOnly({ children }: { children: ReactNode }) {
  const { user, loading } = useSession();
  const search = useSearch();
  if (loading) return <FullPageLoading />;
  if (user) return <Redirect to={safeNext(new URLSearchParams(search).get("next")) ?? "/planos"} replace />;
  return <>{children}</>;
}

// Living style guide (docs/15, BLD-24): development only, never part of the production bundle.
const DesignPage = import.meta.env.DEV ? lazy(() => import("./pages/DesignPage.tsx").then((m) => ({ default: m.DesignPage }))) : null;

function Routes() {
  return (
    <Switch>
      {DesignPage && (
        <Route path="/design">
          <Suspense fallback={<Skeleton lines={6} />}><DesignPage /></Suspense>
        </Route>
      )}
      <Route path="/">
        <Redirect to="/planos" replace />
      </Route>
      <Route path="/login">
        <AnonymousOnly>
          <LoginPage />
        </AnonymousOnly>
      </Route>
      <Route path="/recuperar">
        <AnonymousOnly>
          <RequestResetPage />
        </AnonymousOnly>
      </Route>
      {/* Reset works whether or not someone is logged in on this browser: the token decides. */}
      <Route path="/redefinir/:token">{(p) => <ResetPasswordPage token={p.token} />}</Route>
      <Route path="/convite/:token">{(p) => <InvitationPage token={p.token} />}</Route>
      <Route path="/planos">
        <Protected>
          <PlansPage />
        </Protected>
      </Route>
      <Route path="/planos/:planId">{(p) => <Redirect to={`/planos/${p.planId}/painel`} replace />}</Route>
      <Route path="/planos/:planId/acesso-encerrado">
        {(p) => (
          <Protected>
            <AccessEndedPage planId={p.planId} />
          </Protected>
        )}
      </Route>
      <Route path="/planos/:planId/*">
        {(p) => (
          <Protected>
            <PlanShell planId={p.planId}>
              <PlanRoutes planId={p.planId} />
            </PlanShell>
          </Protected>
        )}
      </Route>
      <Route path="/perfil">
        <Protected>
          <ProfilePage />
        </Protected>
      </Route>
      {/* Institutional maturity module (BLD-26): instructor-only, outside any plan. */}
      <Route path="/diagnosticos"><Protected><AssessmentsPage /></Protected></Route>
      <Route path="/diagnosticos/modelos"><Protected><TemplatesPage /></Protected></Route>
      <Route path="/diagnosticos/modelos/:id/versoes/:version">
        {(p) => <Protected><TemplateVersionPage key={p.version} templateId={p.id} versionId={p.version} /></Protected>}
      </Route>
      <Route path="/diagnosticos/:id/avaliacao/:scenario">
        {(p) => p.scenario === "diagnosis" || p.scenario === "prognosis"
          ? <Protected><ScenarioPage key={`${p.id}-${p.scenario}`} id={p.id} kind={p.scenario} /></Protected>
          : <NotFoundPage />}
      </Route>
      <Route path="/diagnosticos/:id/atribuicoes">{(p) => <Protected><AssignmentsPage key={p.id} id={p.id} /></Protected>}</Route>
      <Route path="/diagnosticos/:id/painel">{(p) => <Protected><MaturityDashboardPage key={p.id} id={p.id} /></Protected>}</Route>
      <Route path="/diagnosticos/:id/relatorios">{(p) => <Protected><MaturityReportsPage key={p.id} id={p.id} /></Protected>}</Route>
      <Route>
        <NotFoundPage />
      </Route>
    </Switch>
  );
}

/** Routes inside a plan. Absolute patterns (no wouter nesting) so every link stays absolute. */
function PlanRoutes({ planId }: { planId: string }) {
  const base = "/planos/:planId";
  return (
    <Switch>
      <Route path={`${base}/painel`}><DashboardPage /></Route>
      <Route path={`${base}/planejamento`}><PlanningPage /></Route>
      <Route path={`${base}/agenda`}><AgendaPage /></Route>
      <Route path={`${base}/agenda/:id`}>{(p) => <AgendaEventPage key={p.id} id={p.id} />}</Route>
      <Route path={`${base}/relatorios`}><ReportsPage /></Route>
      <Route path={`${base}/diagnostico`}><WorkshopDiagnosisPage /></Route>
      <Route path={`${base}/diagnostico/:assessmentId/:kind/:categoryId`}>
        {(p) => p.kind === "diagnosis" || p.kind === "prognosis"
          ? <WorkshopCategoryPage key={`${p.assessmentId}-${p.kind}-${p.categoryId}`} assessmentId={p.assessmentId} kind={p.kind} categoryId={p.categoryId} />
          : <NotFoundInPlan planId={planId} />}
      </Route>
      <Route path={`${base}/revisoes`}><ReviewQueuePage /></Route>
      <Route path={`${base}/revisoes/:id`}>{(p) => <RevisionPage key={p.id} id={p.id} />}</Route>
      <Route path={`${base}/iniciativas/:id`}>{(p) => <InitiativePage key={p.id} id={p.id} />}</Route>
      <Route path={`${base}/configuracoes/:tab?`}>{(p) => <SettingsPage tab={p.tab} />}</Route>
      <Route><NotFoundInPlan planId={planId} /></Route>
    </Switch>
  );
}

function NotFoundInPlan({ planId }: { planId: string }) {
  usePageTitle("Página não encontrada");
  return (
    <div className="max-w-md">
      <h1 id="page-title" tabIndex={-1} className="text-xl font-bold outline-none">Página não encontrada</h1>
      <p className="mt-2 text-sm text-muted">Este conteúdo não existe ou não está disponível nesta oficina.</p>
      <GuardedLink href={`/planos/${planId}/painel`} className="tap mt-3 inline-flex text-sm font-semibold text-primary">Ir para o painel</GuardedLink>
    </div>
  );
}

export function App() {
  return (
    <SessionProvider>
      <ToastProvider>
        <GuardProvider>
          <RouteEffects />
          <RouteAnnouncer />
          <Routes />
        </GuardProvider>
      </ToastProvider>
    </SessionProvider>
  );
}
